import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ShieldCheck, ShieldOff } from "lucide-react";
import { api, errMsg } from "@/lib/galleryApi";

const ACTIONS = {
  studio_grant: "Droit Studio attribue / modifie", studio_revoke: "Droit Studio retire", gallery_create: "Galerie creee",
  gallery_settings: "Reglages de securite modifies", gallery_delete: "Galerie supprimee definitivement", link_regenerate: "Lien de partage regenere",
  publish: "Publication", purge: "Suppression definitive de photos", delivery: "Envoi de la galerie par e-mail",
};

function GrantRow({ u, orgs, onSaved }) {
  const ps = u.photo_studio || {};
  const [all, setAll] = useState(!!ps.all_orgs);
  const [ids, setIds] = useState(new Set(ps.org_ids || []));
  const save = async () => {
    try { onSaved(u.id, await api.post(`/photo-studio/access/${u.id}`, { all_orgs: all, org_ids: [...ids] })); toast.success(`Droit Studio accorde a ${u.email}`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const revoke = async () => {
    try { await api.del(`/photo-studio/access/${u.id}`); onSaved(u.id, { enabled: false }); toast.success("Droit Studio retire"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (u.is_super_admin) return <li className="py-3 text-xs"><p className="text-white">{u.name || u.email} <span className="text-[#10B981]">· super-administrateur (acces automatique)</span></p></li>;
  return (
    <li className="py-3 space-y-2" data-testid={`studio-user-${u.id}`}>
      <div className="flex items-center gap-2 text-xs">
        {ps.enabled ? <ShieldCheck className="w-4 h-4 text-[#10B981]" /> : <ShieldOff className="w-4 h-4 text-[#64748B]" />}
        <span className="text-white flex-1 truncate">{u.name || u.email} <span className="text-[#64748B]">{u.email}</span></span>
        {ps.enabled && <Button size="sm" variant="ghost" onClick={revoke} className="h-7 text-red-400" data-testid={`studio-revoke-${u.id}`}>Retirer</Button>}
      </div>
      <label className="flex items-center gap-2 text-xs text-[#94A3B8]"><Checkbox checked={all} onCheckedChange={(v) => setAll(!!v)} data-testid={`studio-all-${u.id}`} />Toutes les organisations</label>
      {!all && (
        <div className="max-h-28 overflow-y-auto grid sm:grid-cols-2 gap-1 pl-1">
          {orgs.map((o) => (
            <label key={o.id} className="flex items-center gap-2 text-[11px] text-[#94A3B8] truncate">
              <Checkbox checked={ids.has(o.id)} onCheckedChange={(v) => setIds((s) => { const n = new Set(s); v ? n.add(o.id) : n.delete(o.id); return n; })} />{o.org_name || o.id}
            </label>
          ))}
        </div>
      )}
      <Button size="sm" onClick={save} disabled={!all && !ids.size} className="h-7 text-xs bg-[#0099CC] text-white" data-testid={`studio-grant-${u.id}`}>{ps.enabled ? "Mettre a jour le perimetre" : "Accorder l'acces Studio"}</Button>
    </li>
  );
}

export function StudioAccessDialog({ open, onOpenChange }) {
  const [data, setData] = useState({ users: [], organizations: [] });
  const [audit, setAudit] = useState([]);
  useEffect(() => {
    if (!open) return;
    api.get("/photo-studio/access").then(setData).catch((e) => toast.error(errMsg(e)));
    api.get("/photo-studio/audit").then(setAudit).catch(() => {});
  }, [open]);
  const onSaved = (id, ps) => setData((d) => ({ ...d, users: d.users.map((u) => (u.id === id ? { ...u, photo_studio: ps } : u)) }));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0B1120] border-[#1A2332] text-white w-[calc(100vw-1.5rem)] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="studio-access-dialog">
        <DialogHeader>
          <DialogTitle className="text-left">Acces Studio Photo</DialogTitle>
          <DialogDescription className="text-[#94A3B8]">Seuls les collaborateurs GLOTECH (comptes administrateurs hors organisations clientes) peuvent recevoir ce droit. Les comptes des organisations clientes n'y ont jamais acces.</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="users">
          <TabsList className="bg-[#060B14] border border-[#1A2332]"><TabsTrigger value="users">Collaborateurs</TabsTrigger><TabsTrigger value="audit" data-testid="studio-audit-tab">Historique</TabsTrigger></TabsList>
          <TabsContent value="users">
            <ul className="divide-y divide-[#1A2332]">{data.users.map((u) => <GrantRow key={u.id} u={u} orgs={data.organizations} onSaved={onSaved} />)}</ul>
          </TabsContent>
          <TabsContent value="audit">
            <ul className="divide-y divide-[#1A2332] text-xs" data-testid="studio-audit-list">
              {audit.length === 0 && <li className="py-3 text-[#64748B]">Aucun evenement</li>}
              {audit.map((a) => (
                <li key={a.id} className="py-2">
                  <p className="text-white">{ACTIONS[a.action] || a.action} {a.details?.target_email && <span className="text-[#0099CC]">→ {a.details.target_email}</span>}</p>
                  <p className="text-[#64748B]">{new Date(a.at).toLocaleString("fr-FR")} · {a.user_email}{a.details?.title ? ` · ${a.details.title}` : ""}{a.details?.photos ? ` · ${a.details.photos} photos` : ""}</p>
                </li>
              ))}
            </ul>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
